/**
 * Eyebrow — kleines Caps-Label mit Akzent-Linie davor.
 *
 * Brand-Diktat (FRONTEND_GUIDELINES.md):
 *   - Sehr häufig auf der Buchungsseite als Section-Marker eingesetzt
 *   - Inter Tight 500/12, uppercase, letter-spacing 0.16em
 *   - 22px Strich in Akzent-Farbe DAVOR (CSS via globals.css `.eyebrow::before`)
 *
 * Varianten:
 *   - default (accent): grüne Linie + grüner Text
 *   - muted: ohne Linie, in fg-mute (für Stat-Tile-Sub-Labels)
 *   - on-dark: heller für bg-dark Sections
 */
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

const eyebrowVariants = cva(
  ['font-sans font-semibold', 'uppercase tracking-[0.16em]', 'inline-flex items-center gap-2.5'],
  {
    variants: {
      variant: {
        // Signature-Eyebrow: sealwax „—" + sealwax-Uppercase-Label.
        // Marker-Rot ist Identity, matched Word-Split-Pattern in Headlines.
        default:
          'text-sealwax text-xs ' +
          'before:content-[""] before:w-[22px] before:h-px before:bg-sealwax',
        muted: 'text-ink-faint text-xs tracking-[0.08em]',
        sub: 'text-ink-faint text-[11px] tracking-[0.06em]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface EyebrowProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof eyebrowVariants> {}

export const Eyebrow = forwardRef<HTMLSpanElement, EyebrowProps>(
  ({ className, variant, ...props }, ref) => (
    <span ref={ref} className={cn(eyebrowVariants({ variant }), className)} {...props} />
  ),
);
Eyebrow.displayName = 'Eyebrow';
